import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Download, Truck, RotateCcw } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = [
  "placed",
  "packed",
  "shipped",
  "on_rikshaw",
  "out_for_delivery",
  "delivered",
  "cancelled",
  "returned",
];

export default function AdminOrderDetail() {
  const { order_no } = useParams();
  const [order, setOrder] = useState(null);
  const [rikshaws, setRikshaws] = useState([]);
  const [assignRid, setAssignRid] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/orders/${order_no}`);
    setOrder(data);
    setNewStatus(data.status);
    setAssignRid(data.assigned_rikshaw_id || "");
  };

  useEffect(() => {
    load();
    api.get("/admin/rikshaws").then((r) => setRikshaws(r.data.items || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_no]);

  if (!order) return <p className="label-overline text-jow-navy/50">Loading…</p>;

  const changeStatus = async () => {
    if (newStatus === order.status) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.order_no}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const assignRikshaw = async () => {
    if (!assignRid) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.order_no}/assign-rikshaw`, {
        rikshaw_id: assignRid,
      });
      toast.success(`Assigned to ${assignRid}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const refundMark = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.order_no}/refund-mark`);
      toast.success("Marked as refunded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      const token = localStorage.getItem("jow_token");
      const resp = await fetch(
        `${API}/admin/orders/${order.order_no}/invoice.pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${order.order_no}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF.");
    }
  };

  const canRefund =
    order.payment?.status === "paid" &&
    ["cancelled", "cancelled_after_hold", "returned"].includes(order.status);

  return (
    <div className="space-y-5" data-testid={`admin-order-${order.order_no}`}>
      <Link to="/admin/orders" className="label-overline text-jow-pink inline-flex items-center gap-1">
        <ChevronLeft size={13} /> All orders
      </Link>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-overline text-jow-gold">
            {order.type === "reservation" ? "Reservation" : "Order"}
          </span>
          <h1 className="font-display text-3xl text-jow-navy mt-1">{order.order_no}</h1>
          <p className="text-xs text-jow-navy/55 mt-1">
            {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={downloadInvoice}
          className="btn-secondary-jow text-[11px]"
          data-testid="download-invoice"
        >
          <Download size={13} /> Download invoice PDF
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Customer + address */}
        <section className="bg-white border border-jow-navy/10 p-5 space-y-3" data-testid="order-address">
          <p className="label-overline text-jow-navy">Customer</p>
          <p className="text-sm text-jow-navy">
            <strong>{order.address?.name}</strong>
            <br />
            {order.address?.line1}
            {order.address?.line2 ? `, ${order.address.line2}` : ""}
            <br />
            {order.address?.city} {order.address?.state} {order.address?.pincode}
            <br />
            {order.address?.phone}
          </p>
        </section>

        {/* Status + assignment */}
        <section className="bg-white border border-jow-navy/10 p-5 space-y-3" data-testid="order-actions">
          <p className="label-overline text-jow-navy">Controls</p>
          <div>
            <label className="text-xs text-jow-navy/60">Status</label>
            <div className="flex gap-2 mt-1">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="flex-1 border border-jow-navy/15 px-3 py-2 text-sm bg-white"
                data-testid="status-select"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              <button
                onClick={changeStatus}
                disabled={busy || newStatus === order.status}
                className="btn-primary-jow text-[11px]"
                data-testid="status-apply"
              >
                Apply
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-jow-navy/60">Assign rikshaw</label>
            <div className="flex gap-2 mt-1">
              <select
                value={assignRid}
                onChange={(e) => setAssignRid(e.target.value)}
                className="flex-1 border border-jow-navy/15 px-3 py-2 text-sm bg-white"
                data-testid="rikshaw-select"
              >
                <option value="">— select —</option>
                {rikshaws.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} · {r.city}
                  </option>
                ))}
              </select>
              <button
                onClick={assignRikshaw}
                disabled={busy || !assignRid}
                className="btn-primary-jow text-[11px]"
                data-testid="rikshaw-apply"
              >
                <Truck size={13} />
              </button>
            </div>
            {order.assigned_rikshaw_id && (
              <p className="text-[10px] text-jow-navy/50 mt-1">
                Current: {order.assigned_rikshaw_id}
              </p>
            )}
          </div>

          {canRefund && (
            <button
              onClick={refundMark}
              className="btn-secondary-jow text-[11px] border-jow-pink text-jow-pink w-full"
              data-testid="refund-mark"
            >
              <RotateCcw size={13} /> Mark refunded
            </button>
          )}
        </section>
      </div>

      {/* Items */}
      <section className="bg-white border border-jow-navy/10 p-5">
        <p className="label-overline text-jow-navy mb-3">Items</p>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-jow-navy/50">
            <tr>
              <th className="text-left py-2">Item</th>
              <th className="text-right py-2">Qty</th>
              <th className="text-right py-2">Price</th>
              <th className="text-right py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it) => {
              const price = it.price_inr ?? it.price ?? 0;
              const lineTotal = it.line_total ?? price * (it.qty || 1);
              return (
                <tr key={it.sku} className="border-t border-jow-navy/5">
                  <td className="py-2 flex items-center gap-3">
                    {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover" />}
                    <div>
                      <p className="text-jow-navy">{it.name}</p>
                      <p className="text-[10px] text-jow-navy/50">{it.sku}</p>
                    </div>
                  </td>
                  <td className="text-right">{it.qty ?? 1}</td>
                  <td className="text-right">₹{price.toLocaleString("en-IN")}</td>
                  <td className="text-right font-display">
                    ₹{lineTotal.toLocaleString("en-IN")}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="text-sm">
            <tr>
              <td colSpan={3} className="text-right pt-3 text-jow-navy/60">Subtotal</td>
              <td className="text-right pt-3">₹{(order.subtotal ?? 0).toLocaleString("en-IN")}</td>
            </tr>
            {order.coupon && (
              <tr>
                <td colSpan={3} className="text-right text-jow-navy/60">
                  Coupon ({order.coupon.code})
                </td>
                <td className="text-right text-jow-pink">
                  -₹{(order.coupon.discount_amount ?? order.discount ?? 0).toLocaleString("en-IN")}
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={3} className="text-right text-jow-navy/60">Discount</td>
              <td className="text-right">-₹{(order.discount ?? 0).toLocaleString("en-IN")}</td>
            </tr>
            <tr>
              <td colSpan={3} className="text-right text-jow-navy/60">Shipping + GST</td>
              <td className="text-right">
                ₹{((order.shipping ?? 0) + (order.tax ?? 0)).toLocaleString("en-IN")}
              </td>
            </tr>
            {order.payment?.hold_fee_inr ? (
              <tr>
                <td colSpan={3} className="text-right text-jow-navy/60">Reservation hold fee</td>
                <td className="text-right">
                  ₹{(order.payment.hold_fee_inr ?? 0).toLocaleString("en-IN")}
                </td>
              </tr>
            ) : null}
            <tr className="font-display text-jow-navy">
              <td colSpan={3} className="text-right pt-2 border-t border-jow-navy/10">Total</td>
              <td className="text-right pt-2 border-t border-jow-navy/10">
                ₹{(order.total ?? 0).toLocaleString("en-IN")}
              </td>
            </tr>
          </tfoot>
        </table>
      </section>

      {/* Timeline */}
      <section className="bg-white border border-jow-navy/10 p-5">
        <p className="label-overline text-jow-navy mb-3">Timeline</p>
        <ol className="space-y-3 text-sm">
          {(order.timeline || []).map((t, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-2 h-2 mt-1.5 bg-jow-gold rounded-full" />
              <div className="flex-1">
                <p className="text-jow-navy capitalize">{t.status.replaceAll("_", " ")}</p>
                <p className="text-[10px] text-jow-navy/50">
                  {new Date(t.at).toLocaleString()} · {t.note}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
